<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import {
  AtomLoadingSpinner,
  SatItemBuilder,
  useToast,
  type SatItemBuilderEmits,
} from "@chatfood/design-system";
import { computed, ref } from "vue";
import type { ItemBuilderEmits } from "./emits";
import type { ItemBuilderProps } from "./props";
import { friendlyTimeDuration, useAnalytics, useBugReporter } from "#imports";
import { useAddItemBasketErrorValidation } from "~/composables/use-add-item-basket-error-validation";
import { useLocale } from "~/composables/use-locale";
import { useBasketStore } from "~/stores/basket";
import { useBusinessStore } from "~/stores/business";
import { useFeatureMatrixStore } from "~/stores/featureMatrix";
import { useItemStore } from "~/stores/item";

const props = defineProps<ItemBuilderProps>();
const emits = defineEmits<ItemBuilderEmits>();

const css = bemBuilder("item-builder");

const { toastError } = useToast();
const { report } = useBugReporter();
const { trackEvent } = useAnalytics();
const { t } = useLocale();

const business = useBusinessStore();
const basket = useBasketStore();
const itemStore = useItemStore();
const featureMatrix = useFeatureMatrixStore();

const isAdding = ref(false);

const isLoading = computed(() => itemStore.isProcessing);

const item = computed(() => {
  const isRootItemVariant = itemStore.state.type === "variant";

  return {
    ...itemStore.state,
    modifierLists: itemStore.state.modifierLists.map((list) => ({
      ...list,
      modifiers: list.modifiers.map((modifier) => ({
        ...modifier,
        // Ensure inStock is true for variant items
        // since we don't have the field at this level
        inStock: isRootItemVariant ? true : modifier.inStock,
      })),
    })),
  };
});

const { handleBasketError } = useAddItemBasketErrorValidation();

async function onAddToBasket(payload: SatItemBuilderEmits["addToBasket"][0]) {
  isAdding.value = true;

  try {
    await basket.addItem({
      ...payload,
      menuId: itemStore.menuId ?? props.menuId,
      maxSelection: itemStore.maxSelection,
    });

    trackEvent({
      name: "addItemToBasket",
      payload: {
        id: payload.id,
        name: itemStore.name,
        quantity: payload.quantity,
        price: {
          value: payload.unitPrice,
          currency: basket.currency,
        },
      },
    });

    onClose();
  } catch (error) {
    const wasHandled = handleBasketError(
      error,
      item.value.modifierLists,
      payload.id,
    );

    if (wasHandled) return;

    report(error);
    toastError(t("components.item_builder.add_item_general_error"));
  } finally {
    isAdding.value = false;
  }
}

const onClose = () => {
  emits("close");
};

const itemQuantityLimits = computed(() => {
  const basketLimits = basket.itemLimits?.[item.value.plu];
  const itemMax = item.value.maxSelection;

  const remainingQuantity =
    basketLimits?.quantity.remaining ?? itemMax ?? undefined;
  const maxQuantity = basketLimits?.quantity.maximum ?? itemMax;

  return {
    maxQuantity: remainingQuantity,
    maxQuantityLabel: maxQuantity
      ? t("components.item_builder.max_quantity_is", {
          maxQuantity,
        })
      : undefined,
  };
});

const warningMessage = computed(() => {
  const minimumLeadTimeInMinutes = itemStore.minimumLeadTimeInMinutes;

  const friendlyTimeInAdvance = friendlyTimeDuration({
    minutes: minimumLeadTimeInMinutes,
    translationConfig: { t },
  });

  if (minimumLeadTimeInMinutes > 0) {
    return t("components.item_builder.minimum_lead_time_warning", {
      timeIndAdvance: friendlyTimeInAdvance,
    });
  }

  return "";
});
</script>

<template>
  <div :class="css('')">
    <div v-if="isLoading" :class="css('loading')">
      <AtomLoadingSpinner />
    </div>
    <SatItemBuilder
      v-else
      :item="item"
      :max-item-quantity="itemQuantityLimits.maxQuantity"
      :max-quantity-label="itemQuantityLimits.maxQuantityLabel"
      :discount-applied="basket.discount"
      :item-display-view="business.menuDisplayLayout === 'grid' ? 'col' : 'row'"
      :is-cta-loading="isAdding"
      :display-notes="featureMatrix.isToggleEnabled('collect-item-notes')"
      :warning-message="warningMessage"
      :is-cta-disabled="!!warningMessage"
      @add-to-basket="onAddToBasket"
      @close="onClose"
    />
  </div>
</template>

<style lang="scss" scoped>
.item-builder {
  position: relative;
  max-width: 100%;
  height: 100%;
  min-height: 400px;

  &__loading {
    max-width: 100%;
    width: 100%;
    height: 100dvh;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (width > $breakpoint-sm) {
      width: 580px;
      height: 580px;
    }
  }
}
</style>
